import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plus, TrashSimple, PencilSimple } from "@phosphor-icons/react";

const empty = {
  name: "", start_time: "08:00", end_time: "17:00",
  break_minutes: 30, off_days: [5, 6], grace_minutes: 10,
  overtime_after_minutes: 30, flexi: false,
};
const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Shifts() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/shifts");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleDay = (d) => {
    setForm((f) => ({
      ...f,
      off_days: f.off_days.includes(d) ? f.off_days.filter((x) => x !== d) : [...f.off_days, d],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        break_minutes: parseInt(form.break_minutes, 10),
        grace_minutes: parseInt(form.grace_minutes, 10),
        overtime_after_minutes: parseInt(form.overtime_after_minutes, 10),
      };
      if (editingId) {
        await api.patch(`/shifts/${editingId}`, payload);
        toast.success("Shift updated");
      } else {
        await api.post("/shifts", payload);
        toast.success("Shift created");
      }
      setForm(empty);
      setEditingId(null);
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name, start_time: s.start_time, end_time: s.end_time,
      break_minutes: s.break_minutes, off_days: s.off_days || [],
      grace_minutes: s.grace_minutes, overtime_after_minutes: s.overtime_after_minutes,
      flexi: !!s.flexi,
    });
    setShowForm(true);
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try {
      await api.delete(`/shifts/${s.id}`);
      toast.success("Shift deleted");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="px-4 md:px-10 py-8 max-w-7xl mx-auto" data-testid="shifts-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-eyebrow">Schedules</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter text-zinc-950 mt-1">Shifts</h1>
          <p className="text-zinc-600 mt-1 text-sm">{items.length} shifts defined.</p>
        </div>
        <button
          data-testid="new-shift-btn"
          onClick={() => { setEditingId(null); setForm(empty); setShowForm((v) => !v); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold rounded-sm"
        >
          <Plus size={14} weight="bold" />
          New Shift
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-sm p-5 mb-6 grid sm:grid-cols-3 gap-4" data-testid="shift-form">
          <div className="sm:col-span-3 label-eyebrow">{editingId ? "Edit shift" : "New shift"}</div>
          <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="lom-input" data-testid="shift-name-input" /></Field>
          <Field label="Start time"><input type="time" required value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="lom-input" /></Field>
          <Field label="End time"><input type="time" required value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="lom-input" /></Field>
          <Field label="Break (minutes)"><input type="number" min={0} required value={form.break_minutes} onChange={(e) => setForm({ ...form, break_minutes: e.target.value })} className="lom-input" /></Field>
          <Field label="Grace period (minutes)"><input type="number" min={0} required value={form.grace_minutes} onChange={(e) => setForm({ ...form, grace_minutes: e.target.value })} className="lom-input" data-testid="shift-grace-input" /></Field>
          <Field label="Overtime after (minutes past end)"><input type="number" min={0} required value={form.overtime_after_minutes} onChange={(e) => setForm({ ...form, overtime_after_minutes: e.target.value })} className="lom-input" /></Field>
          <div className="sm:col-span-3">
            <label className="label-eyebrow block mb-2">Off days</label>
            <div className="flex flex-wrap gap-2">
              {dayLabels.map((d, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleDay(i)}
                  className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider border ${form.off_days.includes(i) ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300"}`}
                  data-testid={`off-day-${i}`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-3 flex items-center gap-2">
            <input id="flexi" type="checkbox" checked={form.flexi} onChange={(e) => setForm({ ...form, flexi: e.target.checked })} />
            <label htmlFor="flexi" className="text-sm">Flexi shift (no late/early classification)</label>
          </div>
          <div className="sm:col-span-3 flex gap-2 justify-end">
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2 border border-zinc-300 rounded-sm text-sm">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm text-sm" data-testid="shift-submit-btn">
              {editingId ? "Save changes" : "Create shift"}
            </button>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((s) => (
          <div key={s.id} className="bg-white border border-zinc-200 rounded-sm p-5" data-testid={`shift-card-${s.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-heading font-bold text-lg text-zinc-950">{s.name}</div>
                <div className="text-sm text-zinc-600 font-mono">{s.start_time} – {s.end_time}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => startEdit(s)} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid={`edit-shift-${s.id}`}><PencilSimple size={14} /></button>
                <button onClick={() => remove(s)} className="p-1.5 hover:bg-red-50 text-red-600 rounded-sm" data-testid={`delete-shift-${s.id}`}><TrashSimple size={14} /></button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div><div className="label-eyebrow">Break</div><div className="font-mono mt-0.5">{s.break_minutes}m</div></div>
              <div><div className="label-eyebrow">Grace</div><div className="font-mono mt-0.5">{s.grace_minutes}m</div></div>
              <div><div className="label-eyebrow">OT after</div><div className="font-mono mt-0.5">{s.overtime_after_minutes}m</div></div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {dayLabels.map((d, i) => (
                <span key={i} className={`text-[0.65rem] px-1.5 py-0.5 rounded-sm uppercase font-bold tracking-wider ${s.off_days?.includes(i) ? "bg-zinc-100 text-zinc-400 line-through" : "bg-emerald-50 text-emerald-700"}`}>{d}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label className="label-eyebrow block mb-1.5">{label}</label>{children}</div>
);
